import { useRouter } from 'next/navigation';
import { Info } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { InfoModal } from '@/components/ui/InfoModal/InfoModal';
import Button from '@/components/ui/Button';
import { useOAuthHref } from '@/contexts/OAuthFlowContext';

// Gears with a display label (gearUnsupported.gearLabels.*); others fall back
// to the raw gear value, matching the prior GEAR_DISPLAY_LABELS behavior.
const LABELLED_GEARS = new Set(['amp', 'amp-cab', 'pedal', 'outboard']);

interface Props {
  selectGears: string[];
  onClose: () => void;
  isArchitectureUnsupported?: boolean;
}

export function GearUnsupportedDialog({
  selectGears,
  isArchitectureUnsupported,
  onClose,
}: Props) {
  const router = useRouter();
  const oauthHref = useOAuthHref();
  const t = useTranslations();
  const locale = useLocale();
  const labels = selectGears.map((g) =>
    LABELLED_GEARS.has(g) ? t(`gearUnsupported.gearLabels.${g}`) : g
  );
  const gearLabel =
    locale === 'zh-CN'
      ? labels.join('、')
      : labels.length <= 2
        ? labels.join(' and ')
        : `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;

  const tone3000Link = (
    <a href="https://tone3000.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
      tone3000.com
    </a>
  );

  return (
    <InfoModal
      open
      onClose={onClose}
      title={t('gearUnsupported.title')}
      icon={<Info size={24} className="text-white" />}
      message={
        <div className="flex flex-col gap-4">
          {isArchitectureUnsupported ? (
            <>
              <span className="text-zinc-400 text-sm">{t('gearUnsupported.architectureMessage')}</span>
              <span className="text-zinc-400 text-sm">
                {t('gearUnsupported.forMoreTones')} {tone3000Link}
              </span>
            </>
          ) : (
            <>
              <span className="text-zinc-400 text-sm">{t('gearUnsupported.viewingOnly', { gearLabel })}</span>
              <span className="text-zinc-400 text-sm">
                {t('partner.forFullLibrary')} {tone3000Link}
              </span>
            </>
          )}
        </div>
      }
      action={
        <Button
          isPrimary
          onClick={() => {
            router.push(oauthHref('/api/v1/select'));
            window.scrollTo({ top: 0, behavior: 'auto' });
            onClose();
          }}
          variant="slim"
          className="w-full"
        >
          {t('gearUnsupported.browseCompatible')}
        </Button>
      }
    />
  );
}
